// Lib
import { forOwn } from 'lodash';

// Services
import localStorageService, { localStorageFeatureTest } from '../../../utils/services/localStorage/localStorage';

/**
 * Feature detection extracted from Modernizr's source:
 * https://github.com/Modernizr/Modernizr/tree/master/feature-detects
 * see modernizr_LICENSE
 */
function createElement(...args) {
    if (typeof document.createElement !== 'function') {
        // This is the case in IE7, where the type of createElement is "object".
        // For this reason, we cannot call apply() as Object is not a Function.
        return document.createElement(args[0]);
    }
    return document.createElement.apply(document, args);
}

// Content editable
const contentEditableTest = () => {
    // early bail out
    if (!('contentEditable' in document.documentElement)) {
        return;
    }

    // some mobile browsers (android < 3.0, iOS < 5) claim to support
    // contentEditable, but but don't really. This test checks to see
    // confirms whether or not it actually supports it.

    const div = createElement('div');
    div.contentEditable = true;
    return div.contentEditable === 'true';
};

// SVGs
const svgTest = () =>
    !!document.createElementNS && !!document.createElementNS('http://www.w3.org/2000/svg', 'svg').createSVGRect;

// Web sockets
const webSocketsTest = () => {
    const protocol = 'https:' == location.protocol ? 'wss' : 'ws'; // eslint-disable-line

    if ('WebSocket' in window) {
        const protoBin = 'binaryType' in WebSocket.prototype;
        if (protoBin) {
            return protoBin;
        }
        try {
            return !!new WebSocket(`${protocol}://.`).binaryType;
        } catch (e) {
            // Do nothing
        }
    }
    return false;
};

// End Modernizr feature detection
export const LOCAL_STORAGE_FEATURE_NAME = 'localStorage';
const REQUIRED_FEATURES = {
    [LOCAL_STORAGE_FEATURE_NAME]: {
        test: localStorageFeatureTest,
    },
    contentEditable: {
        test: contentEditableTest,
    },
    svg: {
        test: svgTest,
    },
    webSockets: {
        test: webSocketsTest,
    },
};

/**
 * Check if the required features work on this browser.
 */
export const validateRequiredFeatures = () => {
    let isSupported = true;
    const errors = [];

    forOwn(REQUIRED_FEATURES, (featureData, featureName) => {
        const featureTestResult = featureData.test();
        if (!featureTestResult) {
            console.warn(`FEATURE WARNING: Required feature ${featureName} is not supported by this browser`);
            errors.push(featureName);
        }
        isSupported = isSupported && featureTestResult;
    });

    return {
        isSupported,
        errors,
    };
};

export const isSupportedBrowser = () => {
    const supportedBrowserRegex = /Safari|Chrome|Firefox|iPhone|iPad/;
    const unsupportedBrowserRegex = /Edge|MSIE|rv:11/;

    const userAgent = window.navigator.userAgent || '';
    return supportedBrowserRegex.test(userAgent) && !unsupportedBrowserRegex.test(userAgent);
};

const BROWSER_UNSUPPORTED_ACKNOWLEDGEMENT_KEY = 'unsupportedAcknowledgement';

export const getBrowserUnsupportedAcknowledgement = () =>
    localStorageService.getItem(BROWSER_UNSUPPORTED_ACKNOWLEDGEMENT_KEY) === 'true';

export const setBrowserUnsupportedAcknowledgement = () =>
    localStorageService.setItem(BROWSER_UNSUPPORTED_ACKNOWLEDGEMENT_KEY, true);
